import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface DocumentPageInfo {
    selected: boolean;
    rotation: 0 | 90 | 180 | 270;
}

export interface DocumentPagesState {
    [documentId: string]: {
        pages: {
            [pageNumber: number]: DocumentPageInfo;
        };
        totalPages: number;
        currentPage: number;
    };
}

const initialState: DocumentPagesState = {};

const slice = createSlice({
    name: "DocumentPages",
    initialState,
    reducers: {
        setDocumentPages(
            state,
            action: PayloadAction<{ documentId: string; totalPages: number }>
        ) {
            const { documentId, totalPages } = action.payload;

            // Check if document already exists in state
            const existingDocument = state[documentId];

            if (existingDocument && existingDocument.totalPages === totalPages) {
                // Document exists with same page count - preserve existing state
                // Only update currentPage if it's invalid
                if (existingDocument.currentPage > totalPages || existingDocument.currentPage < 1) {
                    existingDocument.currentPage = 1;
                }
                return;
            }

            // Initialize pages - preserve existing selections if document exists
            const pages: { [pageNumber: number]: DocumentPageInfo } = {};
            for (let i = 1; i <= totalPages; i++) {
                // Use existing page state if available, otherwise default to selected
                const existingPage = existingDocument?.pages?.[i];
                pages[i] = existingPage || { selected: true, rotation: 0 };
            }

            state[documentId] = {
                pages,
                totalPages,
                currentPage: existingDocument?.currentPage && existingDocument.currentPage <= totalPages
                    ? existingDocument.currentPage
                    : 1,
            };
        },

        togglePageSelection(
            state,
            action: PayloadAction<{ documentId: string; pageNumber: number }>
        ) {
            const { documentId, pageNumber } = action.payload;
            const document = state[documentId];
            if (!document) return;

            if (document.pages[pageNumber]) {
                document.pages[pageNumber].selected = !document.pages[pageNumber].selected;
            }
        },

        rotatePage(
            state,
            action: PayloadAction<{
                documentId: string;
                pageNumber: number;
                rotation: 0 | 90 | 180 | 270;
            }>
        ) {
            const { documentId, pageNumber, rotation } = action.payload;
            const document = state[documentId];
            if (!document || !document.pages[pageNumber]) return;

            document.pages[pageNumber].rotation = rotation;
        },

        setCurrentPage(
            state,
            action: PayloadAction<{ documentId: string; currentPage: number }>
        ) {
            const { documentId, currentPage } = action.payload;
            const document = state[documentId];
            if (!document) return;

            document.currentPage = currentPage;
        },

        clearDocumentPages(state) {
            return {};
        },

        removeDocumentPages(
            state,
            action: PayloadAction<{ documentId: string }>
        ) {
            const { documentId } = action.payload;
            delete state[documentId];
        },
    },
});

export const {
    setDocumentPages,
    togglePageSelection,
    rotatePage,
    setCurrentPage,
    clearDocumentPages,
    removeDocumentPages,
} = slice.actions;

// Selectors
export const getDocumentPages = (state: any, documentId: string) =>
    state.DocumentPages[documentId];

export const getSelectedPages = (state: any, documentId: string) => {
    const document = state.DocumentPages[documentId];
    if (!document) return [];

    return Object.entries(document.pages)
        .filter(([_, pageInfo]) => (pageInfo as DocumentPageInfo).selected)
        .map(([pageNumber, pageInfo]) => ({
            pageNumber: parseInt(pageNumber),
            rotation: (pageInfo as DocumentPageInfo).rotation,
        }));
};

/**
 * Returns page information for a document only if it differs from the default state
 * (i.e., some pages are excluded or have non-zero rotation).
 * Returns undefined if all pages are in default state (all selected, no rotation).
 */
export const getDocumentPagesForJob = (state: any, documentId: string): Array<{ pageNumber: number; rotation?: number; excluded?: boolean }> | undefined => {
    const document = state.DocumentPages[documentId];
    if (!document) return undefined;

    const allPages = Object.entries(document.pages);
    const modifiedPages: Array<{ pageNumber: number; rotation?: number; excluded?: boolean }> = [];

    // Check each page for deviations from default state
    allPages.forEach(([pageNumber, pageInfo]) => {
        const info = pageInfo as DocumentPageInfo;
        const pageNum = parseInt(pageNumber);

        const isExcluded = !info.selected;
        const isRotated = info.rotation !== 0;

        // Only include pages that differ from default (selected: true, rotation: 0)
        if (isExcluded || isRotated) {
            const pageData: { pageNumber: number; rotation?: number; excluded?: boolean } = {
                pageNumber: pageNum
            };

            if (isRotated) {
                pageData.rotation = info.rotation;
            }

            if (isExcluded) {
                pageData.excluded = true;
            }

            modifiedPages.push(pageData);
        }
    });

    return modifiedPages.length > 0 ? modifiedPages : undefined;
};

/**
 * Collects document pages for job creation across multiple documents.
 * Only includes page information when it differs from default state.
 */
export const getDocumentPagesForJobBatch = (
    state: any,
    documents: Array<{ id: number | string }>
): { [documentId: string]: Array<{ pageNumber: number; rotation?: number; excluded?: boolean }> } | undefined => {
    const documentPages: { [documentId: string]: Array<{ pageNumber: number; rotation?: number; excluded?: boolean }> } = {};

    documents.forEach((doc) => {
        const pages = getDocumentPagesForJob(state, doc.id.toString());
        if (pages && pages.length > 0) {
            documentPages[doc.id.toString()] = pages;
        }
    });

    return Object.keys(documentPages).length > 0 ? documentPages : undefined;
};

export default slice.reducer; 